/**
 * Copilot agent <-> client contract.
 *
 * An assistant turn is an ordered list of content BLOCKS streamed over
 * AGENT_EVENTS. The same event objects flow over the in-memory mock transport
 * today and named SSE events later (mirror of the DeepResearch
 * researchStream/researchProgress pattern) — every event carries a monotonic
 * `ts` so a polling fallback can replay with `?since=cursor`.
 *
 * Client -> agent communication is CLIENT_EVENTS; structured interactions
 * (quick replies, CTA clicks, confirmations, OAuth results) reference the
 * block that requested them via `blockId`.
 */

export const BLOCK = {
  TEXT: "text", // { markdown } — filled incrementally via block_delta
  QUICK_REPLIES: "quick_replies", // { prompt, replyId, options: [{value,label,description?}], selectedValue? }
  CTA_CHIPS: "cta_chips", // { chips: [{actionId,label,icon?,style?,tableId?,path?}], clickedActionId? }
  TABLE_REF: "table_ref", // { tableId, title, totalCount, columns: [{key,label,width?}] }
  PROGRESS: "progress", // { status: running|done|failed, label, messages: [], percent?, detail? }
  CONFIRMATION: "confirmation", // { confirmId, prompt, confirmLabel, cancelLabel, resolution? }
  CONNECT_INTEGRATION: "connect_integration", // { provider, actionId, label, description, status: pending|connected|failed }
  SUMMARY_CARD: "summary_card", // { title, rows: [{label,value,tone?}], footnote? }
  ERROR_CTA: "error_cta", // { title, markdown, severity: warn|error, chips }
  // { previewId, kind: "sequence_builder", title, status: building|done|failed,
  //   steps: [{type, subject?, body?, linkedin_action?, message?, days_later}],
  //   sequenceId?, autoOpen? } — view-only side panel showing the agent's
  //   mutation happening in realtime; patched via block_update.
  LIVE_PREVIEW: "live_preview",
  // { questions: [{question, header?, options: [{label, description?}]}],
  //   done?, answers?, skipped? } — structured requirement-gathering: the
  //   agent asks BEFORE generating; answered via QUESTION_FORM_RESPONSE.
  QUESTION_FORM: "question_form",
  // { kind, title, items: [{id, title, subtitle?, badges?: [string],
  //   fields: [{label, value}], detail: {...}}] } — list-shaped tool results
  //   rendered as cards; clicking one opens its full `detail` in the canvas.
  ENTITY_CARDS: "entity_cards",
  // { name, steps: [{type, subject?, body?, message?, linkedin_action?,
  //   automated?, notes?, days_later}], status?, clickedActionId? } —
  //   pre-create draft review: builder-style step cards + Create CTA
  DRAFT_REVIEW: "draft_review"
};

export const AGENT_EVENTS = {
  TURN_START: "turn_start", // { turnId }
  BLOCK_START: "block_start", // { turnId, block }
  BLOCK_DELTA: "block_delta", // { turnId, blockId, delta } — append to text markdown
  BLOCK_UPDATE: "block_update", // { turnId, blockId, patch } — shallow merge
  BLOCK_END: "block_end", // { turnId, blockId }
  TURN_END: "turn_end", // { turnId, await: user_input|quick_reply|cta|confirmation|action_result|question_form|null, hint? }
  AGENT_ERROR: "agent_error", // { turnId?, message }
  // { items: [{kind: "reply"|"action", label, actionId?, tableId?, path?, icon?}] }
  // — chips for the next user turn: reply chips send text, action chips drive
  // the UI (open table, navigate). Plain strings normalize to reply chips.
  SUGGESTIONS: "suggestions"
};

export const CLIENT_EVENTS = {
  USER_MESSAGE: "user_message", // { text }
  QUICK_REPLY_SELECTED: "quick_reply_selected", // { blockId, replyId, value }
  CTA_CLICKED: "cta_clicked", // { blockId, actionId }
  CONFIRMATION_RESPONSE: "confirmation_response", // { blockId, confirmId, confirmed }
  ACTION_RESULT: "action_result", // { blockId, actionId, status: success|failed|dismissed, data? }
  QUESTION_FORM_RESPONSE: "question_form_response" // { blockId, answers: [{question, answer|null}] }
};

export const AWAIT = {
  USER_INPUT: "user_input",
  QUICK_REPLY: "quick_reply",
  CTA: "cta",
  CONFIRMATION: "confirmation",
  ACTION_RESULT: "action_result",
  QUESTION_FORM: "question_form"
};

// Chip action ids the CLIENT resolves locally (in addition to reporting the
// click to the agent). Anything else is agent-side only.
export const LOCAL_ACTIONS = {
  VIEW_TABLE: "view_table", // chip must carry `tableId`
  NAVIGATE: "navigate", // chip must carry `path` (in-app route)
  CONTACT_SUPPORT: "contact_support" // opens Intercom (same as product-wide pattern)
};

// Product-wide support entry point (UpgradePopup, banners, etc. use the same).
export const openSupportChat = () => {
  if (window.Intercom) {
    window.Intercom("show");
  }
};
